import { ForwardedRef, forwardRef, useState } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { IconButton } from "../button/icon-button";
import { Input, InputProps } from "../forms/input";
import { EyeClosedIcon } from "../icons/eye-closed-icon";
import { EyeIcon } from "../icons/eye-icon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip/tooltip";
import { FormField, TFormField } from "./form-field";

export type PasswordFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = TFormField<TFieldValues, TFieldPath> &
  Omit<InputProps, "after" | "autoComplete" | "children" | "type">;

export const PasswordFormField = forwardRef(function PasswordFormField<
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: PasswordFormFieldProps<TFieldValues, TFieldPath>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FormField {...props}>
      {(field) => (
        <Input
          ref={ref}
          after={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    tabIndex={-1}
                    icon={showPassword ? <EyeClosedIcon /> : <EyeIcon />}
                    onClick={() => setShowPassword((prev) => !prev)}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {showPassword ? "Hide password" : "Show password"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
          autoComplete={showPassword ? "off" : undefined}
          type={showPassword ? "text" : "password"}
          {...props}
          {...field}
        />
      )}
    </FormField>
  );
});
